import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import ContractorPortalApi from "../../../../../infrastructure/api/contractorPortalApi";
import { formatNumber } from "../../../../../infrastructure/utils/formatters";
import ProgressTimeline from "../components/ProgressTimeline";
import { uploadProgressAttachments } from "../utils/progressAttachmentUpload";

const ContractorProjectProgressTab = ({ executionId, annexId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [percent, setPercent] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    const res = await ContractorPortalApi.getProgress(executionId);
    if (!res?.isSuccess) {
      setError(res?.message || "خطا در بارگذاری پیشرفت.");
      setData(null);
    } else {
      setData(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const value = Number(percent);
    if (Number.isNaN(value) || value < 0 || value > 100) {
      toast.error("درصد پیشرفت باید بین ۰ تا ۱۰۰ باشد.");
      return;
    }
    if (!annexId) {
      toast.error("شناسه پیوست قرارداد در دسترس نیست.");
      return;
    }

    setSubmitting(true);
    try {
      let attachments = [];
      if (files.length) {
        attachments = await uploadProgressAttachments(files, "SLN");
        if (attachments.some((a) => !a.sha256 && !a.content)) {
          toast.error("محاسبه هش پیوست ناموفق بود. لطفاً دوباره تلاش کنید.");
          setSubmitting(false);
          return;
        }
      }

      const res = await ContractorPortalApi.submitProgress(annexId, {
        percentComplete: value,
        description,
        attachments,
      });
      if (!res?.isSuccess) {
        toast.error(res?.message || "ثبت پیشرفت ناموفق بود.");
      } else {
        toast.success("گزارش پیشرفت ثبت شد.");
        setPercent("");
        setDescription("");
        setFiles([]);
        await load();
      }
    } catch (err) {
      toast.error(err?.message || "خطا در ثبت پیشرفت.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Stack spacing={2} data-testid="contractor-progress-tab">
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            پیشرفت فعلی:{" "}
            {data?.currentProgressPercent == null
              ? "—"
              : `${formatNumber(data.currentProgressPercent)}٪`}
          </Typography>
        </CardContent>
      </Card>

      <ProgressTimeline reports={data?.reports || []} />

      {data?.canSubmit !== false ? (
        <Card variant="outlined" data-testid="progress-submit-form">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              ثبت گزارش پیشرفت
            </Typography>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="درصد پیشرفت"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  required
                  fullWidth
                  data-testid="progress-percent-input"
                />
                <TextField
                  label="توضیحات"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  data-testid="progress-description-input"
                />
                  <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    پیوست تصاویر / مدارک
                  </Typography>
                  <Button variant="outlined" component="label">
                    انتخاب فایل
                    <input
                      hidden
                      type="file"
                      accept="image/*,application/pdf"
                      multiple
                      data-testid="progress-attach-input"
                      onChange={(e) =>
                        setFiles(Array.from(e.target.files || []))
                      }
                    />
                  </Button>
                  {files.length ? (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {files.map((f) => f.name).join("، ")}
                    </Typography>
                  ) : null}
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  data-testid="progress-submit-button"
                >
                  {submitting ? "در حال ارسال..." : "ارسال گزارش"}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info">ثبت پیشرفت برای این وضعیت اجرا مجاز نیست.</Alert>
      )}
    </Stack>
  );
};

export default ContractorProjectProgressTab;
